import { ref, watch, computed, onMounted } from 'vue';
import { useInternational } from '@/common/locale';
import router from '@/common/router';
import { useViewModeStore } from '@/common/stores/view-mode';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { usePropertyStore } from '@/common/stores/property';
import { storeToRefs } from 'pinia';
import { getLocaleKey } from '@/common/utils/string.utils';
import { webStorageController } from '@/common/utils/browser';
import { LOGIN_CUSTOMER } from '@/common/define/common.define';

export const setup = () => {
  const { t } = useInternational();
  const { viewMode } = storeToRefs(useViewModeStore());
  const baseMenuStore = useBaseMenuStore();
  const propertyStore = usePropertyStore();
  const { mainPanelInfo } = storeToRefs(baseMenuStore);
  const { loginCustomer } = storeToRefs(propertyStore);
  const STORAGE_KEY = 'showTutorialTooltip';
  const tutorialViewItems = [
    'dashboard',
    'infrastructure',
    'kubernetes',
    'application',
    'pa',
    'database',
  ];
  const tutorialViewKey = ref<string>('');
  const tutorialInfo = computed(() => ({
    title: t('TUTORIAL.TOOLTIP.TITLE', {
      viewMode: t(`WORD.${viewMode.value.toUpperCase()}`),
      value: t(`WORD.GNB.${getLocaleKey(tutorialViewKey.value)}`),
    }),
    description:
      tutorialViewKey.value === 'dashboard'
        ? t('TUTORIAL.TOOLTIP.DESCRIPTION_DASHBOARD')
        : t('TUTORIAL.TOOLTIP.DESCRIPTION'),
  }));
  const showTooltip = ref(false);

  const goToTutorial = () => {
    router.push(`/tutorial/${tutorialViewKey.value}`);
  };

  const hideTooltip = () => {
    showTooltip.value = false;
  };

  const dismissTooltip = () => {
    webStorageController.setItem({
      type: 'local',
      key: STORAGE_KEY,
      value: 'hidden',
    });
    hideTooltip();
  };

  watch(
    () => mainPanelInfo.value.selectedCategory,
    (category) => {
      const showTutorialTooltip = webStorageController.getItem({
        type: 'local',
        key: STORAGE_KEY,
      });
      if (
        !category ||
        !tutorialViewItems.includes(category) ||
        showTutorialTooltip === 'hidden' ||
        loginCustomer.value === LOGIN_CUSTOMER.LGE
      ) {
        showTooltip.value = false;
        tutorialViewKey.value = '';
      } else {
        showTooltip.value = true;
        tutorialViewKey.value = category === 'pa' ? 'analysis' : category;
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    const { isFromTutorial } = history.state;

    if (isFromTutorial && showTooltip.value) {
      hideTooltip();
    }
  });

  return {
    t,
    tutorialInfo,
    showTooltip,
    goToTutorial,
    hideTooltip,
    dismissTooltip,
  };
};
